import * as entities from '@owl-nest/entities'
import * as shopify from '@owl-nest/shopify'
import type { Pageable, ProductReviewPage } from '@owl-nest/api-client/latest'
import type { SlimCold } from '@owl-nest/cold-data/client'
import { getMatchingBrand } from '@boutique/contentful-shopify-matcher'

import * as brand from '../brand'

export const SIZE_ALIASES = ['pointure', 'pointures', 'size', 'sizes', 'taille', 'tailles']

export type Product = Omit<entities.product.MediumProduct, 'vendor' | 'brand'> & {
  brand: brand.SlimColdBrand | brand.SlimColdUnmatchedBrand
  deliveryExtra: string | null
  hasUniquePrice: boolean
  hasUniqueCompareAtPrice: boolean
  images: entities.base.Image[]
  isGiftCard: boolean
  isReturnable: boolean
  numericId: string
  options: Option[]
  productionDays: number | null
  reviews: Pageable<ProductReviewPage> | null
  seo?: {
    description: string | null
    title: string | null
  }
  sizeGuideImage: string | null
  sizeGuideText: string | null
  sticker: 'on-demand' | 'delivered-later' | null
  subtitle: string | null
  totalInventory: number | null
  variants: Variant[]
  vendor: brand.SlimColdBrand
}

type Variant = {
  availableForSale: boolean
  currentlyNotInStock: boolean
  compareAtPrice: entities.base.Price | null
  id: string
  image: entities.base.Image
  numericId: number
  price: entities.base.Price
  quantityAvailable: number | null
  requiresShipping: boolean
  selectedOptions: { name: string; value: string }[]
}

export type Option = {
  name: string
  values: string[]
}

export function product(
  id: string,
  shopifyProduct: shopify.product.Product,
  apiReviews: Pageable<ProductReviewPage> | void,
  cold: SlimCold,
): Product | null {
  const mappedMediumProduct = entities.product.mediumProduct(shopifyProduct, cold)

  let vendor: brand.SlimColdBrand
  try {
    vendor = getMatchingBrand(mappedMediumProduct.vendor, cold)
  } catch (exception) {
    return null
  }

  let brand: brand.SlimColdBrand | brand.SlimColdUnmatchedBrand

  if (mappedMediumProduct.brand !== mappedMediumProduct.vendor) {
    try {
      brand = getMatchingBrand(mappedMediumProduct.brand, cold)
    } catch (exception) {
      brand = { name: mappedMediumProduct.brand }
    }
  } else {
    brand = vendor
  }

  return {
    ...mappedMediumProduct,
    brand: brand,
    deliveryExtra: shopifyProduct.deliveryExtra?.value ?? null,
    images: shopifyProduct.images.edges.map(({ node: image }) => ({
      alt: image.altText,
      origin: image.originalSrc,
      src: image.transformedSrc1x ?? image.originalSrc,
      srcSet: `${image.transformedSrc1x} 1x, ${image.transformedSrc2x} 2x, ${image.transformedSrc3x} 3x`,

      // Height & width are always those of the original image, and the
      // gallery relies on those for internal scaling/ratio.
      // To avoid gallery ratio screw-up, we relie on image auto-scaling on load
      height: null,
      width: null,
    })),
    isReturnable: !shopifyProduct.tags.includes('non-returnable'),
    numericId: id,
    options: shopifyProduct.options
      .filter((option) => {
        if (shopifyProduct.options.length === 1) {
          return true
        }
        if (option.name === 'Title' && option.values.length === 1) {
          return option.values[0] !== 'Default Title'
        }
        return true
      })
      .sort((a, b) => {
        if (SIZE_ALIASES.includes(a.name.toLowerCase())) {
          return -1
        } else if (SIZE_ALIASES.includes(b.name.toLowerCase())) {
          return 1
        } else {
          return 0
        }
      }),
    productionDays: shopifyProduct.productionDays?.value ?? null,
    reviews: apiReviews ?? null,
    seo: shopifyProduct.seo,
    sizeGuideImage: shopifyProduct.sizeGuideImage?.reference?.image.originalSrc ?? null,
    sizeGuideText: shopifyProduct.sizeGuideText?.value ?? null,
    sticker: productSticker(shopifyProduct),
    subtitle: shopifyProduct.subtitle?.value ?? null,
    totalInventory: shopifyProduct.totalInventory,
    isGiftCard: shopifyProduct.productType === 'Gift Cards',
    hasUniquePrice: isUniquePrice(shopifyProduct),
    hasUniqueCompareAtPrice: isUniqueCompareAtPrice(shopifyProduct),
    variants: shopifyProduct.variants.edges.map(({ node: variant }) => {
      return {
        availableForSale: variant.availableForSale,
        currentlyNotInStock: variant.currentlyNotInStock,
        id: variant.id,
        image: {
          alt: variant.image.altText,
          height: variant.image.height,
          origin: variant.image.originalSrc,
          src: variant.image.transformedSrc1x ?? variant.image.originalSrc,
          srcSet: `${variant.image.transformedSrc1x} 1x, ${variant.image.transformedSrc2x} 2x, ${variant.image.transformedSrc3x} 3x`,
          width: variant.image.width,
        },
        numericId: shopify.utils.toNumericId(variant.id, 'ProductVariant'),
        price: {
          amount: Number(variant.priceV2.amount),
          currencyCode: variant.priceV2.currencyCode,
        },
        compareAtPrice: getCompareAtPrice(variant),
        quantityAvailable: variant.quantityAvailable,
        requiresShipping: variant.requiresShipping,
        selectedOptions: variant.selectedOptions,
      }
    }),
    vendor: {
      ...vendor,
      identifier: mappedMediumProduct.vendor,
    },
  }
}

function productSticker(shopifyProduct: shopify.product.Product): 'on-demand' | 'delivered-later' | null {
  if (shopifyProduct.tags.includes('prevente')) {
    return 'delivered-later'
  }

  if (shopifyProduct.tags.includes('sur-demande')) {
    return 'on-demand'
  }

  return null
}

function getCompareAtPrice(variant: shopify.product.Variant): entities.base.Price | null {
  if (!variant.compareAtPriceV2) {
    return null
  }
  const priceAmount = parseFloat(variant.priceV2.amount)
  const compareAtPriceAmount = parseFloat(variant.compareAtPriceV2.amount)

  if (priceAmount >= compareAtPriceAmount) {
    // if (priceAmount !== compareAtPriceAmount) {
    //     logger.warn(
    //         `[Shopify] 'price' is higher than 'compareAtPrice' for variant: ${variant.id}`,
    //         {
    //             product,
    //             variant,
    //             values: {
    //                 price: priceAmount,
    //                 compareAtPrice: compareAtPriceAmount
    //             }
    //         }
    //     )
    // }
    return null
  }
  return {
    amount: compareAtPriceAmount,
    currencyCode: variant.compareAtPriceV2.currencyCode,
  }
}

function isUniqueCompareAtPrice(product: shopify.product.Product): boolean {
  const firstVariant = product.variants.edges[0].node
  return product.variants.edges.every(({ node }) => {
    const isIdenticalCurrency = node.priceV2.currencyCode === firstVariant.priceV2.currencyCode

    if (!node.compareAtPriceV2) {
      return false
    }

    const isIdenticalCompare = node.compareAtPriceV2.amount === firstVariant.compareAtPriceV2?.amount

    return isIdenticalCurrency && isIdenticalCompare
  })
}

function isUniquePrice(product: shopify.product.Product): boolean {
  const firstVariant = product.variants.edges[0].node
  return product.variants.edges.every(({ node }) => {
    const isIdenticalCurrency = node.priceV2.currencyCode === firstVariant.priceV2.currencyCode
    const isIdenticalPrice = node.priceV2.amount === firstVariant.priceV2.amount

    if (!node.compareAtPriceV2) {
      return isIdenticalPrice && isIdenticalCurrency
    }

    const isIdenticalCompare = node.compareAtPriceV2.amount === firstVariant.compareAtPriceV2?.amount

    return isIdenticalPrice && isIdenticalCurrency && isIdenticalCompare
  })
}
